// src/pages/PlatformAdmin.js
// Controls the Basic/Premium plan gate for THIS deployment only — each
// client runs its own separate install, so there is no cross-client
// dashboard here, just the one switch for whichever deployment this
// page is opened on. Visible only to accounts with is_platform_admin
// set true directly in the database (see components/Sidebar.js — this
// link is deliberately NOT part of the normal owner/team_leader
// permission bypass, so a future client's own owner never sees it).
import React, { useState, useEffect } from 'react';
import { get, post } from '../utils/api';

const PREMIUM_FEATURES = [
  'Live Dispatch Map',
  'GPS delivery trail in round reports',
  'Audio Checking Assistant',
  'Messages',
  'WhatsApp messaging from round reports (driver phone numbers)',
];

export default function PlatformAdmin() {
  const user = JSON.parse(localStorage.getItem('dms_user') || '{}');
  const [loading, setLoading] = useState(true);
  const [planTier, setPlanTier] = useState('basic');
  const [expiresAt, setExpiresAt] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  async function load() {
    try {
      const data = await get('/api/platform-admin/plan');
      setPlanTier(data.plan_tier);
      // Date input wants YYYY-MM-DD — the backend may return a full
      // timestamp, so trim to just the date portion.
      setExpiresAt((data.premium_expires_at || '').slice(0, 10));
      setIsActive(data.is_active);
    } catch (err) {
      setMessage(`❌ ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    setMessage('');
    try {
      const result = await post('/api/platform-admin/plan', {
        plan_tier: planTier,
        premium_expires_at: expiresAt || null,
      });
      setIsActive(result.is_active);
      setMessage('✅ Plan updated');
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      setMessage(`❌ ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  function extendTrial(days) {
    const base = expiresAt && new Date(expiresAt) > new Date() ? new Date(expiresAt) : new Date();
    base.setDate(base.getDate() + days);
    setExpiresAt(base.toISOString().slice(0, 10));
  }

  if (!user.isPlatformAdmin) {
    return (
      <div className="p-6">
        <div className="bg-red-50 rounded-lg shadow-lg p-6 border border-red-200">
          <h2 className="text-xl font-bold text-red-900">Access Denied</h2>
          <p className="text-red-800 mt-1">This page is restricted to platform administrators.</p>
        </div>
      </div>
    );
  }

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <div>
        <h1 className="text-3xl font-bold mb-2">Platform Admin</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Controls the Basic/Premium plan for this deployment only.
        </p>
      </div>

      {message && (
        <div className={`p-4 rounded-lg ${message.startsWith('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <span className={`text-xs font-bold px-3 py-1 rounded-full ${isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>
            {isActive ? 'PREMIUM ACTIVE' : 'BASIC (Premium inactive or expired)'}
          </span>
        </div>

        <div className="mb-5">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Plan Tier</label>
          <div className="flex gap-3">
            {['basic', 'premium'].map(tier => (
              <button
                key={tier}
                onClick={() => setPlanTier(tier)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border-2 capitalize transition-all ${
                  planTier === tier
                    ? 'border-brand-700 bg-brand-700/5 text-brand-700'
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-brand-500'
                }`}
              >
                {tier}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-5">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Premium Expires
          </label>
          <input
            type="date"
            value={expiresAt}
            onChange={e => setExpiresAt(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
          />
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Past this date, Premium is treated as inactive even if the plan is set to "premium" above —
            no separate step needed to downgrade.
          </p>
        </div>

        <div className="mb-6">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">Quick extend</p>
          <div className="flex gap-2">
            <button onClick={() => extendTrial(15)} className="px-3 py-1.5 text-xs font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200">
              +15 days (trial)
            </button>
            <button onClick={() => extendTrial(30)} className="px-3 py-1.5 text-xs font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200">
              +30 days
            </button>
            <button onClick={() => extendTrial(365)} className="px-3 py-1.5 text-xs font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200">
              +1 year
            </button>
          </div>
        </div>

        <button
          onClick={save}
          disabled={saving}
          className="px-6 py-3 bg-brand-700 text-white rounded-lg hover:bg-brand-500 disabled:bg-gray-400 font-semibold transition-colors"
        >
          {saving ? 'Saving...' : 'Save Plan'}
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <h2 className="text-lg font-bold mb-3 text-brand-700">Premium-only features</h2>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          {PREMIUM_FEATURES.map(f => <li key={f}>{f}</li>)}
        </ul>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
          Everything else (invoices, dispatch, team management, QR-scan, vouchers, etc.) is always
          available regardless of plan — dropping to Basic never locks a client out of Medji itself.
        </p>
      </div>
    </div>
  );
}
